import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

const useGateApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [buildings, setBuildings] = useState([]);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    api.get('/v1/buildings?pageSize=500').then((res) => {
      setBuildings(res.data?.data?.data || res.data?.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/gates?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/gates', formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/gates/${id}`, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/gates/${ids[0]}`);
      } else {
        await api.post('/v1/gates/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const directionLabel = useCallback((val) => {
    const map = { entry: t('gate.entry'), exit: t('gate.exit'), bidirectional: t('gate.bidirectional') };
    return map[val] || val;
  }, [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('gate.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'buildingName', label: t('gate.buildingName'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'direction', label: t('gate.direction'), options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['entry', 'exit', 'bidirectional'],
          renderValue: (v) => directionLabel(v),
        },
        customFilterListOptions: { render: (v) => directionLabel(v) },
        customBodyRender: (value) => directionLabel(value),
      },
    },
    {
      name: 'selfServiceVisitorAllowed', label: t('gate.selfServiceVisitorAllowed'), options: {
        filter: false, sort: false,
        customBodyRender: (value) => (value ? t('gate.selfServicePublic') : t('gate.selfServiceRestricted')),
      },
    },
    { name: 'accessPointCount', label: t('gate.accessPointCount'), options: { filter: false, sort: false } },
    {
      // AKM POC §5.10 — binada en az bir kapı işaretliyse puantaja yalnız işaretli kapılardan geçişler sayılır.
      name: 'countsForTimesheet', label: t('gate.countsForTimesheet'), options: {
        filter: false, sort: false,
        customBodyRender: (value) => (value ? t('common.yes') : t('common.no')),
      },
    },
  ], [t, directionLabel]);

  const directionOptions = useMemo(() => [
    { id: 'entry', name: t('gate.entry') },
    { id: 'exit', name: t('gate.exit') },
    { id: 'bidirectional', name: t('gate.bidirectional') },
  ], [t]);

  const objectSchema = useMemo(() => [
    {
      name: 'buildingId', label: t('gate.buildingName'), type: 'select', grid: 12, required: true,
      options: buildings, idKey: 'id', labelKey: 'name',
    },
    { name: 'name', label: t('gate.name'), type: 'text', grid: 12, required: true },
    {
      name: 'direction', label: t('gate.direction'), type: 'select', grid: 12, required: true,
      options: directionOptions, idKey: 'id', labelKey: 'name',
    },
    { name: 'isMainEntrance', label: t('gate.isMainEntrance'), type: 'checkbox', grid: 12 },
    { name: 'selfServiceVisitorAllowed', label: t('gate.selfServiceVisitorAllowed'), type: 'checkbox', grid: 12, defaultValue: true },
    {
      name: 'countsForTimesheet', label: t('gate.countsForTimesheet'), type: 'checkbox', grid: 12,
      description: t('gate.countsForTimesheetHint'),
    },
    { name: 'description', label: t('gate.description'), type: 'text', grid: 12 },
  ], [t, buildings, directionOptions]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default useGateApi;
